import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { BarChart3, BookOpen, Brain, Calendar, Check, ClipboardList, Eye, FileText, Heart, Home, Lightbulb, MessageSquare, Phone, Plus, Send, Shield, Trash2, UserCheck, Users, X } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const themes = [
  { id: 'emotions', label: 'Emotions & Feelings', icon: Heart, color: 'rose' },
  { id: 'social', label: 'Social Situations', icon: Users, color: 'blue' },
  { id: 'problem_solving', label: 'Problem Solving', icon: Brain, color: 'purple' },
  { id: 'self_reflection', label: 'Self Reflection', icon: Lightbulb, color: 'yellow' },
  { id: 'general', label: 'General', icon: BookOpen, color: 'gray' }
];

const PrincipalWeeklyQuestions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    week_start: new Date().toISOString().split('T')[0],
    theme: 'general',
    questions: ['']
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadQuestions();
  }, [navigate]);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(`${BACKEND_URL}/api/principal/weekly-questions`, {
        params: { token }
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    setForm({ ...form, questions: [...form.questions, ''] });
  };

  const removeQuestion = (index) => {
    const newQuestions = form.questions.filter((_, i) => i !== index);
    setForm({ ...form, questions: newQuestions });
  };

  const updateQuestion = (index, value) => {
    const newQuestions = [...form.questions];
    newQuestions[index] = value;
    setForm({ ...form, questions: newQuestions });
  };

  const handleSubmit = async () => {
    if (!form.title || form.questions.filter(q => q.trim()).length === 0) {
      alert('Please add a title and at least one question');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/weekly-questions`, null, {
        params: {
          token,
          title: form.title,
          week_start: form.week_start,
          theme: form.theme,
          questions: JSON.stringify(form.questions.filter(q => q.trim()))
        }
      });
      
      alert('Weekly questions created and shared with observers!');
      setShowModal(false);
      setForm({ title: '', week_start: new Date().toISOString().split('T')[0], theme: 'general', questions: [''] });
      loadQuestions();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create questions');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.delete(`${BACKEND_URL}/api/principal/weekly-questions/${questionId}`, {
        params: { token }
      });
      loadQuestions();
    } catch (error) {
      alert('Failed to delete question');
    }
  };

  const getTheme = (themeId) => themes.find(t => t.id === themeId) || themes[4];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="weekly-questions-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Weekly Questions</h1>
              <p className="text-slate-600">Situational questions for observers to discuss with students</p>
            </div>
            <Button onClick={() => setShowModal(true)} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
              <Plus className="w-4 h-4 mr-2" />
              Create New
            </Button>
          </div>

          {/* Questions List */}
          <div className="grid gap-4">
            {questions.length === 0 ? (
              <Card className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Weekly Questions Yet</h3>
                <p className="text-slate-500 mb-4">Create situational questions for observers to discuss with children</p>
                <Button onClick={() => setShowModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Question Set
                </Button>
              </Card>
            ) : (
              questions.map((q, idx) => {
                const theme = getTheme(q.theme);
                const ThemeIcon = theme.icon;
                return (
                  <Card key={q.id || idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className={`w-12 h-12 bg-${theme.color}-100 rounded-xl flex items-center justify-center`}>
                            <ThemeIcon className={`w-6 h-6 text-${theme.color}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{q.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Week of {new Date(q.week_start).toLocaleDateString()}
                              </span>
                              <span className={`px-2 py-0.5 bg-${theme.color}-100 text-${theme.color}-700 rounded-full text-xs`}>
                                {theme.label}
                              </span>
                              <span>{q.questions?.length || 0} questions</span>
                            </div>
                            
                            {/* Questions Preview */}
                            <div className="mt-3 space-y-2">
                              {q.questions?.slice(0, 3).map((question, qIdx) => (
                                <div key={qIdx} className="flex items-start gap-2 text-sm text-slate-700">
                                  <span className="text-blue-500 font-semibold">{qIdx + 1}.</span>
                                  <span>{question}</span>
                                </div>
                              ))}
                              {q.questions?.length > 3 && (
                                <p className="text-sm text-slate-400">+{q.questions.length - 3} more questions</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedQuestion(q)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteQuestion(q.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Create Weekly Questions</span>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    placeholder="e.g., Handling Peer Pressure"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Week Starting</Label>
                  <Input
                    type="date"
                    value={form.week_start}
                    onChange={(e) => setForm({ ...form, week_start: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Theme</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {themes.map((theme) => {
                    const ThemeIcon = theme.icon;
                    return (
                      <button
                        key={theme.id}
                        onClick={() => setForm({ ...form, theme: theme.id })}
                        className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                          form.theme === theme.id
                            ? `border-${theme.color}-500 bg-${theme.color}-50`
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <ThemeIcon className={`w-5 h-5 text-${theme.color}-600`} />
                        <span className="text-sm">{theme.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Questions for Observers</Label>
                <p className="text-sm text-slate-500 mb-2">Add situational questions for observers to discuss with students</p>
                <div className="space-y-2">
                  {form.questions.map((q, idx) => (
                    <div key={idx} className="flex gap-2">
                      <span className="w-6 h-10 flex items-center justify-center text-sm text-slate-500">{idx + 1}.</span>
                      <Textarea
                        placeholder="Enter a thought-provoking question..."
                        value={q}
                        onChange={(e) => updateQuestion(idx, e.target.value)}
                        className="flex-1"
                        rows={2}
                      />
                      {form.questions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(idx)}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addQuestion} className="mt-2">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Question
                </Button>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  Tips for Good Questions
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Ask open-ended questions that encourage discussion</li>
                  <li>• Use age-appropriate scenarios</li>
                  <li>• Focus on feelings and problem-solving</li>
                  <li>• Avoid questions with yes/no answers</li>
                </ul>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-sanjaya-navy hover:bg-sanjaya-navy-dark"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Create & Share
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Question Modal */}
      {selectedQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{selectedQuestion.title}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedQuestion(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  Week of {new Date(selectedQuestion.week_start).toLocaleDateString()}
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Questions:</h4>
                  <div className="space-y-3">
                    {selectedQuestion.questions?.map((q, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                          {idx + 1}
                        </span>
                        <p className="text-slate-700">{q}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    <Check className="w-4 h-4 inline mr-1 text-green-500" />
                    Shared with all observers on {new Date(selectedQuestion.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrincipalWeeklyQuestions;
